import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

export default function Login({ onLogin }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const canSubmit = useMemo(() => {
    return email.trim().length > 3 && pass.length >= 6 && !loading;
  }, [email, pass, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
      const uid = cred.user?.uid;

      if (!uid) {
        setError("No se pudo obtener el usuario.");
        await signOut(auth);
        return;
      }

      // 🔐 Validar admin en Firestore
      const userRef = doc(db, "usuarios", uid);
      const snap = await getDoc(userRef);

     
      // onLogin?.(cred.user); // opcional
      navigate("/admin", { replace: true });
    } catch (e) {
      console.error(e);
      setError("Error al iniciar sesión. Revisa correo y contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowA} aria-hidden="true" />
      <div style={styles.bgGlowB} aria-hidden="true" />

      <div style={styles.wrap}>
        {/* Brand */}
        <div style={styles.brand}>
          <div style={styles.logoWrap}>
            <img
              src="/logo.png"
              alt="MyShopCity"
              style={styles.logoImg}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextSibling;
                if (fallback) fallback.style.display = "flex";
              }}
            />
            <div style={{ ...styles.logoFallback, display: "none" }}>MSC</div>
          </div>

          <div style={styles.brandText}>
            <div style={styles.brandName}>MyShopCity</div>
            <div style={styles.brandTag}>Panel Admin</div>
          </div>
        </div>

        {/* Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h1 style={styles.h1}>Iniciar sesión</h1>
            <p style={styles.sub}>
              Accede al panel para administrar productos y contenido.
            </p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>
              Correo
              <input
                style={styles.input}
                type="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>

            <label style={styles.label}>
              Contraseña
              <input
                style={styles.input}
                type="password"
                placeholder="••••••••"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>

            {error && (
              <div style={styles.errorBox}>
                <div style={styles.errorTitle}>No se pudo iniciar sesión</div>
                <div style={styles.errorText}>{error}</div>
              </div>
            )}

            <button type="submit" disabled={!canSubmit} style={styles.primaryBtn}>
              {loading ? (
                <span style={styles.btnRow}>
                  <span style={styles.spinner} aria-hidden="true" />
                  Validando…
                </span>
              ) : (
                "Entrar"
              )}
            </button>

            <div style={styles.dividerRow}>
              <div style={styles.divider} />
              <span style={styles.dividerText}>o</span>
              <div style={styles.divider} />
            </div>

            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={() => navigate("/", { replace: true })}
              disabled={loading}
            >
              Volver al inicio
            </button>

            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={() => navigate("/crear-cuenta")}
              disabled={loading}
            >
              Crear cuenta / Crear acceso web
            </button>

            <div style={styles.smallNote}>
              Si no eres admin, la sesión se cerrará automáticamente.
            </div>
          </form>
        </div>

        <div style={styles.footer}>
          © {new Date().getFullYear()} MyShopCity • Pagos seguros • Envíos incluidos
        </div>
      </div>
    </div>
  );
}

/* ---------------- Styles ---------------- */
const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: "28px 16px",
    background: "linear-gradient(180deg, #f7f7fb 0%, #ffffff 55%, #f7f7fb 100%)",
    position: "relative",
    overflow: "hidden",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
    color: "#101114",
  },
  bgGlowA: {
    position: "absolute",
    top: -140,
    left: "50%",
    width: 720,
    height: 720,
    transform: "translateX(-55%)",
    background:
      "radial-gradient(circle, rgba(90,120,255,0.20) 0%, rgba(90,120,255,0.06) 35%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  bgGlowB: {
    position: "absolute",
    top: -110,
    left: "50%",
    width: 560,
    height: 560,
    transform: "translateX(-10%)",
    background:
      "radial-gradient(circle, rgba(0,200,255,0.16) 0%, rgba(0,200,255,0.05) 35%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },

  wrap: {
    width: "100%",
    maxWidth: 440,
    position: "relative",
    zIndex: 1,
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    justifyContent: "center",
    marginBottom: 14,
  },
  logoWrap: {
    width: 86,
    height: 86,
    borderRadius: 22,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.95)",
    boxShadow: "0 18px 55px rgba(0,0,0,0.08)",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
  },
  logoImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  logoFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    letterSpacing: 1,
    color: "rgba(16,17,20,0.82)",
    fontSize: 22,
  },
  brandText: { textAlign: "left" },
  brandName: { fontWeight: 950, fontSize: 18, letterSpacing: -0.2, lineHeight: 1.1 },
  brandTag: { fontSize: 12, opacity: 0.7, marginTop: 4 },

  card: {
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.90)",
    boxShadow: "0 18px 65px rgba(0,0,0,0.10)",
    padding: 16,
  },
  cardHeader: { padding: "6px 4px 10px" },
  h1: { margin: 0, fontSize: 22, fontWeight: 950, letterSpacing: -0.3 },
  sub: { margin: "6px 0 0", fontSize: 13, opacity: 0.75, lineHeight: 1.5 },

  form: { display: "grid", gap: 12, marginTop: 8 },

  label: { fontSize: 12, opacity: 0.8, fontWeight: 800, display: "grid", gap: 8 },
  input: {
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.95)",
    outline: "none",
    fontSize: 14,
  },

  errorBox: {
    borderRadius: 14,
    border: "1px solid rgba(255,60,60,0.22)",
    background: "rgba(255,60,60,0.08)",
    padding: 12,
  },
  errorTitle: { fontWeight: 950, fontSize: 13, marginBottom: 4 },
  errorText: { fontSize: 13, opacity: 0.85, lineHeight: 1.45 },

  primaryBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(90,120,255,0.25)",
    background:
      "linear-gradient(180deg, rgba(90,120,255,0.92), rgba(70,100,245,0.92))",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 55px rgba(90,120,255,0.22)",
  },

  secondaryBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.95)",
    color: "rgba(16,17,20,0.86)",
    fontWeight: 950,
    cursor: "pointer",
  },

  dividerRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 10,
    marginTop: 2,
  },
  divider: { height: 1, background: "rgba(0,0,0,0.10)" },
  dividerText: { fontSize: 12, opacity: 0.55, fontWeight: 900 },

  btnRow: { display: "inline-flex", gap: 10, alignItems: "center", justifyContent: "center" },
  spinner: {
    width: 16,
    height: 16,
    borderRadius: 999,
    border: "3px solid rgba(255,255,255,0.35)",
    borderTopColor: "rgba(255,255,255,0.95)",
    animation: "spin 0.9s linear infinite",
  },

  smallNote: { fontSize: 12, opacity: 0.6, textAlign: "center", marginTop: 2 },

  footer: {
    marginTop: 14,
    textAlign: "center",
    fontSize: 12,
    opacity: 0.65,
  },
};

/* ✅ Animación sin CSS externo (incrustada) */
const styleTag = document.createElement("style");
styleTag.innerHTML = `@keyframes spin{to{transform:rotate(360deg)}}`;
document.head.appendChild(styleTag);